// ─── Module « inventory » — service inventaires / stock ───────────
// Levé de MoPCal (services/inventaires.js). SEULE différence de fond :
// le rattachement se fait à l'ESPACE (le tenant de la suite) et non plus au
// calendrier. Le champ historique `calendrierId` reste lu pour rétro-compat :
// un inventaire créé dans MoPCal porte `calendrierId`, un inventaire créé ici
// porte `espaceId`. Le pont espace↔calendrier écrira les deux le jour venu.
//
//   inventaires/{id}            { espaceId, calendrierId?, nom, emoji, arbre,
//                                 proprietes, emplacements?, creePar, creeLe }
//   inventaires/{id}/items/{id} { nom, catId, emoji, description, seuil,
//                                 compteLe, articles[], creePar, creeLe }
//
//   noeud   (récursif) : { id, nom, emoji, enfants:[noeud] }
//   prop               : { id, nom, type:'texte'|'nombre'|'date'|'liste', options? }
//   article            : { id, nom, qte, seuil, vals, photos[], codeBarres?, unique?, emplacementId? }

import { db, niveauDe, rangNiveau, accesDepuisListes, accesDepuisLecteurs } from '@mop/core';
import {
  collection, doc, addDoc, updateDoc, deleteDoc, getDoc, getDocs, setDoc, onSnapshot,
  query, where, writeBatch, deleteField,
} from 'firebase/firestore';

// Types de propriété disponibles (champ « en plus » sur chaque élément).
export const TYPES_PROP = [
  { id: 'texte',  label: 'Texte',          i18n: 'inventaire.typeTexte' },
  { id: 'nombre', label: 'Nombre',         i18n: 'inventaire.typeNombre' },
  { id: 'date',   label: 'Date',           i18n: 'inventaire.typeDate' },
  { id: 'liste',  label: 'Liste de choix', i18n: 'inventaire.typeListe' },
];

// Libellé d'un type de propriété. `t` (useT) optionnel au point d'affichage :
// sans lui, repli sur le label fr embarqué.
export const labelTypeProp = (id, t) => {
  const ty = TYPES_PROP.find(x => x.id === id);
  if (!ty) return id || '';
  return (t && ty.i18n) ? t(ty.i18n) : ty.label;
};

// Génère un identifiant unique (nœud d'arbre, propriété, article).
export function nouvelId(prefixe = 'n') {
  return `${prefixe}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;
}

// ─── Helpers d'arbre (récursifs) ──────────────────────────────────
export function noeudParId(arbre, id) {
  for (const n of (arbre || [])) {
    if (n.id === id) return n;
    const r = noeudParId(n.enfants || [], id);
    if (r) return r;
  }
  return null;
}

// Ids du nœud + de tous ses descendants (inclut `id`).
export function idsDescendants(arbre, id) {
  const n = noeudParId(arbre, id);
  if (!n) return [id];
  const out = [id];
  (function marche(k) {
    (k.enfants || []).forEach(c => { out.push(c.id); marche(c); });
  })(n);
  return out;
}

// Parent d'un nœud : { liste, parent } — `liste` = tableau contenant le nœud.
export function parentDe(arbre, id, parent = null) {
  for (const n of (arbre || [])) {
    if (n.id === id) return { liste: arbre, parent };
    const r = parentDe(n.enfants || [], id, n);
    if (r) return r;
  }
  return null;
}

// Arbre aplati en liste ordonnée [{ id, nom, emoji, profondeur }].
export function aplatirArbre(arbre) {
  const out = [];
  (function marche(noeuds, prof) {
    (noeuds || []).forEach(n => {
      out.push({ id: n.id, nom: n.nom, emoji: n.emoji, profondeur: prof });
      marche(n.enfants || [], prof + 1);
    });
  })(arbre, 0);
  return out;
}

// Chemin d'une catégorie : noms joints par ' › '.
export function cheminDe(arbre, id) {
  const noms = [];
  let cur = id;
  while (cur) {
    const n = noeudParId(arbre, cur);
    if (!n) break;
    noms.unshift(n.nom);
    const p = parentDe(arbre, cur);
    cur = p && p.parent ? p.parent.id : null;
  }
  return noms.join(' › ');
}

// ─── Modèle UNIFIÉ : un élément contient TOUJOURS ≥ 1 article ─────
// L'élément = regroupement (nom, emoji, catId). Tout ce qui décrit l'objet
// physique (quantité, seuil, `vals`, photos, code-barres) vit sur l'article.
// `normaliserItem` rend rétro-compatibles les anciens éléments. Idempotent.
export function normaliserItem(it) {
  if (!it) return it;
  if (Array.isArray(it.articles) && it.articles.length > 0) return it;
  const art = {
    id:         it.id ? ('a_' + it.id) : nouvelId('a'),
    nom:        it.nom || '',
    qte:        Number(it.qte) || 0,
    seuil:      Number(it.seuil) || 0,
    vals:       (it.vals && typeof it.vals === 'object') ? it.vals : {},
    photos:     Array.isArray(it.photos) ? it.photos : [],
    codeBarres: it.codeBarres || null,
    unique:     !!it.unique,
    emplacementId: it.emplacementId || null,
  };
  return { ...it, articles: [art] };
}

export function photosArticle(a) {
  if (Array.isArray(a?.photos) && a.photos.length) return a.photos;
  if (a?.photo) return [a.photo];
  return [];
}

export function articlesDe(item)       { return normaliserItem(item)?.articles || []; }
export function articlePrincipal(item) { return articlesDe(item)[0] || null; }
export function estMono(item)          { return articlesDe(item).length === 1; }
export function estMulti(item)         { return articlesDe(item).length > 1; }
export function estDetaille(item)      { return estMulti(item); }

// Quantité d'un élément = somme des quantités de ses articles.
export function qteItem(item) {
  return articlesDe(item).reduce((s, a) => s + (Number(a.qte) || 0), 0);
}
// Seuil de détection AU NIVEAU ÉLÉMENT (comparé à la quantité totale). 0 = pas d'alerte.
export function seuilItem(item) {
  return Number(item?.seuil) || 0;
}
// Élément sous le seuil (alerte de stock).
export function sousSeuil(item) {
  const s = seuilItem(item);
  return s > 0 && qteItem(item) <= s;
}

// Date du dernier comptage = plus grande `compteLe` parmi les éléments.
export function dernierInventaireDe(items) {
  const ds = (items || []).map(it => it.compteLe).filter(Boolean).sort();
  return ds.length ? ds[ds.length - 1] : null;
}

// ─── Inventaires d'un ESPACE ──────────────────────────────────────
// `tenantId` = id de l'espace actif (cf. useEspace() du Core).
// `onErreur` : REMONTE l'erreur Firestore au lieu de l'avaler. Sans ça, un refus
// de règle se traduit par une liste vide silencieuse — impossible à diagnostiquer.
// `tenants` : soit l'id de l'espace, soit la LISTE tenantIds (espace + tenants
// hérités). Un inventaire créé ici porte `espaceId` ; un inventaire créé par
// MoPCal porte `calendrierId`. On écoute donc DEUX requêtes et on fusionne —
// Firestore ne sait pas faire un OU sur deux champs différents.
export function ecouterInventaires(tenants, callback, onErreur) {
  const ids = (Array.isArray(tenants) ? tenants : [tenants]).filter(Boolean);
  if (!ids.length) { callback([]); return () => {}; }
  const propre = ids[0];                     // l'espace lui-même
  const herites = ids.slice(1, 11);          // `in` est limité à 10 valeurs

  const parSource = { espace: [], herites: [] };
  const emettre = () => {
    const vus = new Set();
    const liste = [];
    for (const d of parSource.espace.concat(parSource.herites)) {
      if (vus.has(d.id)) continue;
      vus.add(d.id); liste.push(d);
    }
    liste.sort((a, b) => (a.creeLe || '').localeCompare(b.creeLe || ''));
    callback(liste);
  };

  const unsubs = [];
  unsubs.push(onSnapshot(
    query(collection(db, 'inventaires'), where('espaceId', '==', propre)),
    (snap) => { parSource.espace = snap.docs.map(d => ({ id: d.id, ...d.data() })); emettre(); },
    (e) => { parSource.espace = []; emettre(); onErreur?.(e); },
  ));
  if (herites.length) {
    unsubs.push(onSnapshot(
      query(collection(db, 'inventaires'), where('calendrierId', 'in', herites)),
      (snap) => { parSource.herites = snap.docs.map(d => ({ id: d.id, ...d.data() })); emettre(); },
      (e) => { parSource.herites = []; emettre(); onErreur?.(e); },
    ));
  }
  return () => unsubs.forEach(u => u && u());
}

// `tenantHerite` : si l'espace a adopté un calendrier MoPCal, on écrit AUSSI
// `calendrierId` → l'inventaire créé ici est immédiatement visible dans MoPCal.
// C'est le partage d'évolution voulu, pas une duplication : un seul document.
// Modèle propriétaire/accès (Phase 2) : liste TOUS les inventaires où l'utilisateur
// a un accès (`acces` = créateur ∪ propriétaire d'espace ∪ lecteurs ∪ rédacteurs),
// À TRAVERS TOUS LES ESPACES — plus de dépendance à l'espace actif. `acces` étant
// dénormalisé, une seule requête array-contains suffit (pas d'index composite).
export function ecouterInventairesAccessibles(uid, callback, onErreur) {
  if (!uid) { callback([]); return () => {}; }
  return onSnapshot(
    query(collection(db, 'inventaires'), where('acces', 'array-contains', uid)),
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => String(a.creeLe || '').localeCompare(String(b.creeLe || '')));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e); },
  );
}

export async function creerInventaire({ tenantId, tenantHerite, nom, emoji, arbre, proprietes, creePar, creeParNom, ownerUid }) {
  if (!tenantId) throw new Error('Aucun espace actif');
  const ref = await addDoc(collection(db, 'inventaires'), {
    espaceId:   tenantId,
    ...(tenantHerite && tenantHerite !== tenantId ? { calendrierId: tenantHerite } : {}),
    // Canon `nom` SEUL (cf. core/elements) : les documents existants ont été
    // migrés (scripts/migrer-canon.js) et MoPCal lit désormais `nom || titre`.
    nom:        (nom || '').trim() || 'Inventaire',
    emoji:      emoji || '📦',
    arbre:      Array.isArray(arbre) ? arbre : [],
    proprietes: Array.isArray(proprietes) ? proprietes : [],
    creePar:    creePar || null,
    creeParNom: creeParNom || '',
    // Partage par défaut : privé (seul le propriétaire). On ajoute ensuite des
    // membres OU « Tout l'espace » depuis Profil → Accès (modèle unifié accesN).
    accesN:     {},
    accesTous:  false,
    lecteurs:   [],
    redacteurs: [],
    acces:      calcAcces({ creePar, ownerUid }),
    creeLe:     new Date().toISOString(),
  });
  return ref.id;
}

export async function renommerInventaire(inventaireId, nom) {
  await updateDoc(doc(db, 'inventaires', inventaireId), { nom: (nom || '').trim() || 'Inventaire' });
}

export async function modifierEmojiInventaire(inventaireId, emoji) {
  await updateDoc(doc(db, 'inventaires', inventaireId), { emoji: emoji || '📦' });
}

// Arbre de catégories complet (tableau de nœuds récursifs).
export async function modifierArbre(inventaireId, arbre) {
  await updateDoc(doc(db, 'inventaires', inventaireId), { arbre: Array.isArray(arbre) ? arbre : [] });
}

// ─── Partage d'un inventaire (par rôle) ───────────────────────────
// Modèle : un espace = un responsable (espace.creePar). Un membre a zéro droit
// par défaut ; le responsable ouvre l'accès par inventaire :
//   • lecteurs  : lecture seule
//   • redacteurs: modifier les DONNÉES (items) — pas la structure ni les props
//   • acces     : union { creePar ∪ ownerUid ∪ lecteurs ∪ redacteurs } —
//                 champ dénormalisé pour la requête (array-contains) et la
//                 règle de lecture. Toujours recalculé ici.
// ── ACCÈS UNIFIÉ (grammaire 3 niveaux du Core) ──
// Source des niveaux : `accesN` = { [uid | '*'] : 'lecteur'|'contributeur'|'gestionnaire' }.
// `acces` (tableau) reste le MIROIR requêtable (array-contains) + gating des
// règles Firestore existantes ; il contient creePar ∪ ownerUid ∪ nominatifs ∪
// (tous les membres si '*'). `lecteurs`/`redacteurs` restent écrits (dérivés)
// pour rétro-compat des anciennes règles.

// Carte des niveaux en mémoire : `accesN` si présent, sinon reconstruite depuis
// les anciennes listes lecteurs/redacteurs (rétro-compat lecture).
export function accesInventaire(inv) {
  if (inv?.accesN && typeof inv.accesN === 'object' && !Array.isArray(inv.accesN)) return inv.accesN;
  return accesDepuisListes(inv?.lecteurs, inv?.redacteurs);
}

// Miroir tableau (array-contains) : creePar ∪ ownerUid ∪ nominatifs (≥lecteur) ∪
// tous les membres si '*'. `membresUids` requis pour matérialiser « Tout l'espace ».
export function calcAcces({ creePar, ownerUid, accesN, membresUids = [] }) {
  const set = new Set();
  if (creePar)  set.add(creePar);
  if (ownerUid) set.add(ownerUid);
  const a = accesN || {};
  if (rangNiveau(a['*']) >= 1) membresUids.forEach(u => u && set.add(u));
  Object.keys(a).forEach(k => { if (k !== '*' && rangNiveau(a[k]) >= 1) set.add(k); });
  return [...set];
}
// Dérive lecteurs/redacteurs (nominatifs) depuis accesN (rétro-compat règles).
export function listesDepuisAccesN(accesN) {
  const a = accesN || {};
  const lec = [], red = [];
  Object.keys(a).forEach(k => {
    if (k === '*') return;
    if (rangNiveau(a[k]) >= 2) red.push(k);
    else if (rangNiveau(a[k]) >= 1) lec.push(k);
  });
  return { lecteurs: lec, redacteurs: red };
}

// Rôle d'un utilisateur SUR un inventaire (pour l'application des droits).
// gestionnaire → contrôle total (comme propriétaire) ; contributeur → données.
export function roleInventaire(inv, uid, ownerUid) {
  if (!uid) return 'aucun';
  if (uid === ownerUid || uid === inv?.creePar) return 'proprietaire';
  const n = niveauDe(accesInventaire(inv), uid, null);
  if (n === 'gestionnaire') return 'proprietaire';
  if (n === 'contributeur') return 'redacteur';
  if (n === 'lecteur')      return 'lecteur';
  return 'aucun';
}

// Enregistre la carte d'accès complète (depuis SectionAcces).
export async function definirAccesInventaire(inventaireId, { creePar, ownerUid, membresUids, acces }) {
  const a = acces || {};
  const { lecteurs, redacteurs } = listesDepuisAccesN(a);
  await updateDoc(doc(db, 'inventaires', inventaireId), {
    accesN:    a,
    acces:     calcAcces({ creePar, ownerUid, accesN: a, membresUids }),
    accesTous: rangNiveau(a['*']) >= 1,
    lecteurs, redacteurs,
  });
}

// Migration douce : remplit `accesN` (depuis les anciennes listes) sur un
// inventaire qui ne l'a pas encore. Idempotent.
export async function assurerAccesInventaire(inv, ownerUid, membresUids = []) {
  if (!inv?.id || (inv.accesN && typeof inv.accesN === 'object')) return;
  const accesN = accesDepuisListes(inv.lecteurs, inv.redacteurs);
  try {
    await updateDoc(doc(db, 'inventaires', inv.id), {
      accesN,
      acces:     calcAcces({ creePar: inv.creePar, ownerUid, accesN, membresUids }),
      accesTous: false,
    });
  } catch (_) { /* pas responsable / règle : on ignore */ }
}

// Ancien helper (allowlist binaire) — conservé pour compat, redirige.
export async function definirLecteursInventaire(inventaireId, lecteurs) {
  await updateDoc(doc(db, 'inventaires', inventaireId), {
    lecteurs: Array.isArray(lecteurs) ? lecteurs : deleteField(),
  });
}

// « Qui voit les plans/emplacements » du tenant (arbre partagé).
export async function definirLecteursPlans(tenantId, lecteurs) {
  if (!tenantId) return;
  await setDoc(doc(db, 'emplacements', tenantId), {
    lecteurs: Array.isArray(lecteurs) ? lecteurs : deleteField(),
  }, { merge: true });
}

// Accès unifié de l'arbre de rangement partagé (modèle accesN). Le read-gate
// des règles reste `lecteurs` : '*' → tout le monde (lecteurs supprimé) ; sinon
// la liste des uids nominatifs. `accesN` conserve les niveaux exacts.
export async function definirAccesPlans(tenantId, acces) {
  if (!tenantId) return;
  const a = acces || {};
  const tous = rangNiveau(a['*']) >= 1;
  const nominatifs = Object.keys(a).filter(k => k !== '*' && rangNiveau(a[k]) >= 1);
  await setDoc(doc(db, 'emplacements', tenantId), {
    accesN: a,
    lecteurs: tous ? deleteField() : nominatifs,
  }, { merge: true });
}
// Carte des niveaux de l'arbre : `accesN` si présent, sinon depuis `lecteurs`
// (absent = tout le monde).
export function accesPlans(lecteurs, accesN) {
  if (accesN && typeof accesN === 'object' && !Array.isArray(accesN)) return accesN;
  return accesDepuisLecteurs(lecteurs, 'lecteur');
}

// Propriétés personnalisées de l'inventaire.
export async function modifierProprietes(inventaireId, proprietes) {
  await updateDoc(doc(db, 'inventaires', inventaireId), { proprietes: Array.isArray(proprietes) ? proprietes : [] });
}

// ─── Modèles de structure ENREGISTRÉS par l'utilisateur ───────────
// La STRUCTURE (arbre de catégories + propriétés, SANS les données) d'un
// inventaire peut être sauvegardée comme modèle réutilisable, pour repartir
// dessus sur un autre inventaire du même compte. C'est distinct de l'export/
// import (qui sert surtout au partage entre comptes). Doc :
//   modelesInventaire/{id} { espaceId, creePar, creeParNom, nom, emoji, desc,
//                            arbre, proprietes, creeLe }
export async function enregistrerModeleInventaire({ tenantId, creePar, creeParNom, nom, emoji, desc, arbre, proprietes }) {
  if (!tenantId) throw new Error('Aucun espace actif');
  const ref = await addDoc(collection(db, 'modelesInventaire'), {
    espaceId: tenantId,
    creePar: creePar || null,
    creeParNom: creeParNom || '',
    nom: (nom || '').trim() || 'Modèle',
    emoji: emoji || '📦',
    desc: desc || '',
    arbre: Array.isArray(arbre) ? arbre : [],
    proprietes: Array.isArray(proprietes) ? proprietes : [],
    creeLe: new Date().toISOString(),
  });
  return ref.id;
}

export function ecouterModelesInventaire(tenants, callback, onErreur) {
  const ids = (Array.isArray(tenants) ? tenants : [tenants]).filter(Boolean).slice(0, 10);
  if (!ids.length) { callback([]); return () => {}; }
  return onSnapshot(
    query(collection(db, 'modelesInventaire'), where('espaceId', 'in', ids)),
    (snap) => {
      const l = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      l.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
      callback(l);
    },
    (e) => { callback([]); onErreur?.(e); },
  );
}

export async function supprimerModeleInventaire(id) {
  await deleteDoc(doc(db, 'modelesInventaire', id));
}

// ─── Emplacements PARTAGÉS au niveau de l'espace ──────────────────
// Un espace a UN seul arbre de rangement, commun à tous ses inventaires.
//   emplacements/{tenantId} = { espaceId, arbre:[noeud], majLe }
export function ecouterEmplacements(tenantId, callback, onErreur) {
  if (!tenantId) { callback([], false); return () => {}; }
  // 3e/4e arguments du callback : `lecteurs` (read-gate) + `accesN` (niveaux
  // unifiés) du doc, pour l'écran d'accès.
  return onSnapshot(doc(db, 'emplacements', tenantId), (s) => {
    const d = s.exists() ? s.data() : null;
    callback(Array.isArray(d?.arbre) ? d.arbre : [], s.exists(), d?.lecteurs, d?.accesN);
  }, (e) => { callback([], false); onErreur?.(e); });
}

export async function modifierEmplacements(tenantId, arbre) {
  if (!tenantId) return;
  await setDoc(doc(db, 'emplacements', tenantId), {
    espaceId: tenantId,
    arbre: Array.isArray(arbre) ? arbre : [],
    majLe: new Date().toISOString(),
  }, { merge: true });
}

// Lecture ONE-SHOT de l'ancien arbre d'emplacements d'un tenant (migration vers
// le modèle « plans multiples » : on récupère l'arbre pour en faire un plan).
export async function lireEmplacementsUneFois(tenantId) {
  if (!tenantId) return [];
  try {
    const snap = await getDoc(doc(db, 'emplacements', tenantId));
    return snap.exists() && Array.isArray(snap.data().arbre) ? snap.data().arbre : [];
  } catch { return []; }
}

// Supprime l'inventaire et tous ses éléments (batch).
export async function supprimerInventaire(inventaireId) {
  const itemsSnap = await getDocs(collection(db, 'inventaires', inventaireId, 'items'));
  const batch = writeBatch(db);
  itemsSnap.forEach(it => batch.delete(it.ref));
  batch.delete(doc(db, 'inventaires', inventaireId));
  await batch.commit();
}

// Lecture ponctuelle d'un inventaire.
export async function lireInventaire(inventaireId) {
  const s = await getDoc(doc(db, 'inventaires', inventaireId));
  return s.exists() ? { id: s.id, ...s.data() } : null;
}

// ─── Éléments d'un inventaire ─────────────────────────────────────
export function ecouterItems(inventaireId, callback) {
  if (!inventaireId) { callback([]); return () => {}; }
  return onSnapshot(
    collection(db, 'inventaires', inventaireId, 'items'),
    (snap) => {
      const items = snap.docs.map(d => normaliserItem({ id: d.id, ...d.data() }));
      items.sort((a, b) => (a.nom || '').localeCompare(b.nom || ''));
      callback(items);
    },
    () => callback([]),
  );
}

export async function ajouterItem(inventaireId, item) {
  const nom = (item?.nom || '').trim();
  if (!nom) return null;
  const src = normaliserItem({ ...item, nom });
  const ref = await addDoc(collection(db, 'inventaires', inventaireId, 'items'), {
    nom,
    catId:       item.catId != null ? item.catId : null,
    emoji:       item.emoji || '',
    description: (item.description || '').trim(),
    seuil:       Number(item.seuil) || 0,
    compteLe:    item.compteLe || null,
    articles:    src.articles,
    creePar:     item.creePar || null,
    creeLe:      new Date().toISOString(),
  });
  return ref.id;
}

// Modification partielle d'un élément (n'écrit que les champs fournis).
export async function modifierItem(inventaireId, itemId, champs) {
  const out = {};
  for (const k of Object.keys(champs || {})) if (champs[k] !== undefined) out[k] = champs[k];
  if (Object.keys(out).length === 0) return;
  await updateDoc(doc(db, 'inventaires', inventaireId, 'items', itemId), out);
}

export async function supprimerItem(inventaireId, itemId) {
  await deleteDoc(doc(db, 'inventaires', inventaireId, 'items', itemId));
}

// Ajuste la quantité de l'article principal (± sur un élément simple).
export async function ajusterQte(inventaireId, item, delta) {
  const arts = articlesDe(item).map((a, i) =>
    i === 0 ? { ...a, qte: Math.max(0, (Number(a.qte) || 0) + delta) } : a);
  await modifierItem(inventaireId, item.id, { articles: arts });
}

// Mode « faire l'inventaire » : marque l'élément comme compté maintenant.
export async function marquerCompte(inventaireId, itemId) {
  await updateDoc(doc(db, 'inventaires', inventaireId, 'items', itemId), { compteLe: new Date().toISOString() });
}

// ─── Import : crée un NOUVEL inventaire depuis un fichier préparé ──
// `prep` vient de preparerImportComplet() : ids déjà régénérés et liens
// (catégories / propriétés / emplacements) reconstruits. L'import est toujours
// ADDITIF — il ne remplace jamais un inventaire existant.
// Les emplacements du fichier sont fusionnés dans l'arbre partagé de l'espace
// (ids conservés → les articles importés restent rangés au bon endroit).
export async function importerInventaire(tenantId, prep, { uid, nom, tenantHerite, tenantRangement } = {}) {
  if (!tenantId) throw new Error('Aucun espace actif');
  const rangement = tenantRangement || tenantId;
  const invId = await creerInventaire({
    tenantId, tenantHerite,
    nom: prep.nom || 'Inventaire importé',
    emoji: prep.emoji || '📦',
    arbre: prep.arbre || [],
    proprietes: prep.proprietes || [],
    creePar: uid, creeParNom: nom || '',
  });

  if (Array.isArray(prep.emplacements) && prep.emplacements.length) {
    const snap = await getDoc(doc(db, 'emplacements', rangement));
    const existant = snap.exists() && Array.isArray(snap.data()?.arbre) ? snap.data().arbre : [];
    const connus = new Set(aplatirArbre(existant).map(o => o.id));
    const nouvelles = prep.emplacements.filter(r => r && !connus.has(r.id));
    if (nouvelles.length) await modifierEmplacements(rangement, existant.concat(nouvelles));
  }

  let n = 0;
  for (const it of (prep.items || [])) {
    try { await ajouterItem(invId, { ...it, creePar: uid }); n += 1; } catch (_) { /* on continue */ }
  }
  return { inventaireId: invId, nbItems: n };
}

// Cherche un article par code-barres parmi les éléments déjà chargés.
// Renvoie { item, article, index } ou null.
export function chercherParCodeBarres(items, code) {
  const c = String(code || '').trim();
  if (!c) return null;
  for (const it of (items || [])) {
    const arts = articlesDe(it);
    const i = arts.findIndex(a => String(a.codeBarres || '') === c);
    if (i >= 0) return { item: it, article: arts[i], index: i };
  }
  return null;
}
